/**
 * The searchable, paged chooser the wizard uses three times.
 *
 * It is not `DataTable`, and the reason is selection. A picker's row is a
 * control, so it has to be a real labelled radio or checkbox that a keyboard
 * reaches and a screen reader announces as chosen. `DataTable` makes its
 * desktop rows clickable `<tr>` elements, which a mouse can use and a keyboard
 * cannot, and nesting a checkbox inside its mobile card button would nest one
 * control in another. So the shape is borrowed and the row is rebuilt here.
 *
 * What is not rebuilt: searching, sorting and paging all stay on the server,
 * because a page is 50 rows of a set that can be much larger.
 */

import { Search } from "lucide-react"
import * as React from "react"

import {
  Button,
  EmptyState,
  ErrorState,
  Input,
  Skeleton,
} from "@/components/ui"
import { PAGE_SIZE } from "@/lib/constants"
import { cn } from "@/lib/utils"

export function PickerShell({
  searchLabel,
  search,
  onSearchChange,
  toolbar,
  rowCount,
  count,
  page,
  onPageChange,
  isLoading,
  isError,
  onRetry,
  emptyTitle,
  emptyDescription,
  emptyAction,
  children,
}: {
  searchLabel: string
  search: string
  onSearchChange: (value: string) => void
  toolbar?: React.ReactNode
  rowCount: number
  count: number
  page: number
  onPageChange: (page: number) => void
  isLoading: boolean
  isError: boolean
  onRetry: () => void
  emptyTitle: string
  emptyDescription?: string
  emptyAction?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className="rounded-lg border">
      <div className="flex flex-wrap items-center gap-3 border-b p-3">
        <div className="relative min-w-56 flex-1">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder={searchLabel}
            aria-label={searchLabel}
            className="pl-9"
          />
        </div>
        {toolbar}
      </div>

      {isError ? (
        <ErrorState description="That list could not be loaded." onRetry={onRetry} />
      ) : isLoading ? (
        <div className="p-4" aria-busy="true" aria-live="polite">
          <span className="sr-only">Loading</span>
          {Array.from({ length: 6 }).map((_, row) => (
            <div key={row} className="flex items-center gap-3 py-3">
              <Skeleton className="h-4 w-4 rounded-full" />
              <Skeleton className="h-4 flex-1" />
            </div>
          ))}
        </div>
      ) : rowCount === 0 ? (
        <EmptyState
          title={emptyTitle}
          {...(emptyDescription ? { description: emptyDescription } : {})}
          {...(emptyAction ? { action: emptyAction } : {})}
        />
      ) : (
        <ul className="max-h-[26rem] divide-y overflow-y-auto">{children}</ul>
      )}

      {!isLoading && !isError && count > 0 && (
        <Pager count={count} page={page} onPageChange={onPageChange} />
      )}
    </div>
  )
}

export function Pager({
  count,
  page,
  onPageChange,
}: {
  count: number
  page: number
  onPageChange: (page: number) => void
}) {
  const lastPage = Math.max(1, Math.ceil(count / PAGE_SIZE))
  const from = count === 0 ? 0 : (page - 1) * PAGE_SIZE + 1
  const to = Math.min(page * PAGE_SIZE, count)

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t p-3 text-sm">
      {/* count is the whole filtered set, so this needs no second request. */}
      <p className="text-muted-foreground">
        {from} to {to} of {count}
      </p>
      <div className="flex items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
        >
          Previous
        </Button>
        <span aria-live="polite">
          Page {page} of {lastPage}
        </span>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= lastPage}
        >
          Next
        </Button>
      </div>
    </div>
  )
}

/**
 * One selectable row. The whole row is the label, so the target is the row and
 * not a 16-pixel box, which is what the touch-friendly brief asks for.
 */
export function ChoiceRow({
  control,
  name,
  selected,
  onSelect,
  title,
  detail,
  trailing,
}: {
  control: "radio" | "checkbox"
  name: string
  selected: boolean
  onSelect: () => void
  title: React.ReactNode
  detail?: React.ReactNode
  trailing?: React.ReactNode
}) {
  return (
    <li>
      <label
        className={cn(
          "flex cursor-pointer items-center gap-3 p-3 hover:bg-muted",
          selected && "bg-primary/10",
        )}
      >
        <input
          type={control}
          name={name}
          checked={selected}
          onChange={onSelect}
          className="h-4 w-4 shrink-0 accent-[var(--color-primary)]"
        />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium">{title}</span>
          {detail && (
            <span className="mt-0.5 block truncate text-xs text-muted-foreground">
              {detail}
            </span>
          )}
        </span>
        {trailing}
      </label>
    </li>
  )
}

/**
 * The row a picker has chosen, and the name that came with the click.
 *
 * Two pieces of state rather than one, because they answer different
 * questions. The id is what gets saved; the name is what the reserved slot
 * shows, and it is kept from the click so the confirmation is filled
 * immediately rather than a request later, and so it survives a search that
 * moves the chosen row off the page. A picker whose caller resumed a draft has
 * no name from a click, and falls back to the row's own record.
 */
export function useChoice(initial: number | null) {
  const [selected, setSelected] = React.useState<number | null>(initial)
  const [name, setName] = React.useState<string | null>(null)

  const choose = React.useCallback((id: number | null, chosen: string | null) => {
    setSelected(id)
    setName(chosen)
  }, [])

  return { selected, name, choose }
}

/** Search term and page, with the term debounced so typing is not a request each. */
export function usePickerParams() {
  const [page, setPage] = React.useState(1)
  const [search, setSearchValue] = React.useState("")
  const [debouncedSearch, setDebouncedSearch] = React.useState("")

  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(timer)
  }, [search])

  const setSearch = React.useCallback((value: string) => {
    setSearchValue(value)
    // A narrower set rarely has the page you were on.
    setPage(1)
  }, [])

  return { page, setPage, search, setSearch, debouncedSearch }
}
